import {parseClauseReference} from "word/taskpane/routes/checklist/utils/apply_document_amendments";

const DIGITS = /^\d+$/;
const SHORT_TOKEN = /^[a-z]{1,4}$/;

// Amendments are grouped per numbered sub-clause, e.g. 25.1 or 16.7.
// A lettered child of a top-level clause (6.a) groups under the clause (6).
export function getUnitKey(reference: string | undefined): string {
  const tokens = parseClauseReference(reference);
  if (tokens.length === 0) {
    return "";
  }
  if (tokens.length === 1 || !DIGITS.test(tokens[1])) {
    return tokens[0];
  }
  return tokens.slice(0, 2).join(".");
}

export function normaliseReference(reference: string | undefined): string {
  return parseClauseReference(reference).join(".");
}

export function isInUnit(
  reference: string | undefined,
  unitKey: string,
): boolean {
  const tokens = parseClauseReference(reference);
  const unitTokens = parseClauseReference(unitKey);
  if (tokens.length < unitTokens.length) {
    return false;
  }
  const sharesPrefix = unitTokens.every((token, i) => tokens[i] === token);
  if (!sharesPrefix) {
    return false;
  }
  // A top-level unit holds only its lettered children, not 25.1 under 25
  if (unitTokens.length === 1 && tokens.length > 1 && DIGITS.test(tokens[1])) {
    return false;
  }
  return true;
}

export function getUnitDepth(
  reference: string | undefined,
  unitKey: string,
): number {
  const depth =
    parseClauseReference(reference).length -
    parseClauseReference(unitKey).length;
  return Math.max(0, depth);
}

function formatToken(token: string): string {
  if (DIGITS.test(token)) {
    return token;
  }
  if (SHORT_TOKEN.test(token)) {
    return `(${token})`;
  }
  return "";
}

// "25.1.a" in unit "25.1" -> "(a)"; "25.1" in unit "25.1" -> "25.1"
export function formatAtomLabel(
  reference: string | undefined,
  unitKey: string,
): string {
  const tokens = parseClauseReference(reference);
  const unitTokens = parseClauseReference(unitKey);
  if (tokens.length === 0) {
    return "";
  }
  if (tokens.length <= unitTokens.length) {
    return tokens.every(token => DIGITS.test(token)) ? tokens.join(".") : "";
  }
  return formatToken(tokens[tokens.length - 1]);
}

export function formatUnitLabel(unitKey: string): string {
  return parseClauseReference(unitKey)
    .map((token, i) => (i === 0 ? token : formatToken(token)))
    .map((token, i) => (i > 0 && DIGITS.test(token) ? `.${token}` : token))
    .join("");
}
